import { StepperGenerationType } from '@/features/video-generation-stepper/types';
import { TemplateName, TemplateCategory, TemplateFeatures, TemplateTag } from '../types/template';
import {
  PROMOTION_VIDEO,
  PRODUCT_COMPARISON_VIDEO,
  PRODUCT_RELEASE_VIDEO,
  BRAND_REVIEW_VIDEO,
  INFLUENCER_PRODUCT_VIDEO,
  PRODUCT_VOICE_OVER_VIDEO,
  FOUNDER_VIDEO,
  BEAUTY_SHOT,
} from '@/constants/text';

export const CATEGORY_LABELS = {
  [TemplateCategory.All]: 'All use cases',
  [TemplateCategory.BrandAwareness]: 'Brand awareness',
  [TemplateCategory.ProductMarketing]: 'Product marketing',
  [TemplateCategory.SalesAndPromotion]: 'Sales and promotion',
} as const;

export const TEMPLATES = [
  {
    id: TemplateName.MagicBackground,
    title: 'Magic background',
    description: 'Generate the perfect background for every product, and then animate the scene. Results are magical',
    stepperGenerationType: StepperGenerationType.bulkStudioVideo,
    category: TemplateCategory.ProductMarketing,
    features: [TemplateFeatures.BulkCreation, TemplateFeatures.ProductFocus],
    videoUrl: '/assets/templates/magical_background.mp4',
    videoDuration: '00:05',
  },
  {
    id: TemplateName.Highlight,
    title: 'Highlight product features',
    description: "Showcase your product's unique details in this dazzling 5-shot video.",
    image: 'assets/templates/highlight_product_features.png',
    stepperGenerationType: StepperGenerationType.bulkStudioVideo,
    category: TemplateCategory.ProductMarketing,
    features: [TemplateFeatures.BulkCreation, TemplateFeatures.ProductFocus],
    videoUrl: '/assets/templates/highlight_product_features.mp4',
    videoDuration: '00:07',
  },
  {
    id: TemplateName.SizingVideos,
    title: 'Sizing videos',
    description: 'Show customers what your product looks like on different-sized models. Proven to decrease returns.',
    image: 'assets/templates/sizing_videos.png',
    stepperGenerationType: StepperGenerationType.bulkStudioVideo,
    category: TemplateCategory.ProductMarketing,
    features: [TemplateFeatures.BulkCreation, TemplateFeatures.ProductFocus],
    tags: [TemplateTag.Fashion],
    videoPosition: 'object-top',
    videoUrl: '/assets/templates/sizing_videos.mp4',
    videoDuration: '00:07',
  },
  {
    id: TemplateName.Lookbook,
    title: 'Animate fashion model',
    description: 'Breathe new life into any page by animating your static model images.',
    image: '/assets/templates/animate_fashion_model.png',
    stepperGenerationType: StepperGenerationType.bulkStudioVideo,
    category: TemplateCategory.ProductMarketing,
    features: [TemplateFeatures.BulkCreation, TemplateFeatures.ProductFocus],
    tags: [TemplateTag.Fashion],
    videoUrl: '/assets/templates/animate_fashion_model_2.mp4',
    videoPosition: 'object-top',
    videoDuration: '00:05',
  },
  {
    id: TemplateName.ProductRotate,
    title: '180° product video',
    description: 'Show off different angles of your product with this video template.',
    image: 'assets/templates/180_product_video.png',
    stepperGenerationType: StepperGenerationType.bulkStudioVideo,
    category: TemplateCategory.ProductMarketing,
    features: [TemplateFeatures.BulkCreation, TemplateFeatures.ProductFocus],
    videoUrl: '/assets/templates/180_product_video_2.mp4',
    videoPosition: 'object-center',
    videoDuration: '00:05',
  },
  {
    id: TemplateName.InfluencerAvatar,
    title: INFLUENCER_PRODUCT_VIDEO,
    description: 'Generate high-quality UGC with AI influencers.',
    image: 'assets/templates/influencer_product_video.png',
    videoUrl: '/assets/templates/influencer_product_video.mp4',
    specs: ['Social-first format', 'Boosts brand relatability', 'Captures attention in seconds'],
    category: TemplateCategory.SalesAndPromotion,
    features: [TemplateFeatures.Avatar, TemplateFeatures.ProductFocus],
    videoDuration: '00:17',
  },
  {
    id: TemplateName.Promotional,
    title: PROMOTION_VIDEO,
    description:
      'Highlight your special offer with a short, on-trend promotion video that inspires viewers to take action',
    image: '',
    videoUrl:
      'https://videos.gotolstoy.com/public/0d49234a-1203-442e-adb2-167a94ff278f/bb2b9c71-f097-4490-9cd5-8843224f5cec/bb2b9c71-f097-4490-9cd5-8843224f5cec_w_.mp4?2024-10-17T09:18:56.084Z',
    specs: ['Drives conversions', 'Highlights special offers', 'Clear call-to-action'],
    category: TemplateCategory.SalesAndPromotion,
    features: [TemplateFeatures.Avatar],
  },
  {
    id: TemplateName.ProductComparison,
    title: PRODUCT_COMPARISON_VIDEO,
    description:
      'Showcase two similar products side by side with a quick, influencer-style comparison that highlights their unique features and helps viewers decide which one suits them best',
    image: '',
    category: TemplateCategory.ProductMarketing,
    features: [TemplateFeatures.Avatar, TemplateFeatures.ProductFocus],
  },
  {
    id: TemplateName.ProductRelease,
    title: PRODUCT_RELEASE_VIDEO,
    description:
      'Build hype and boost sales with a high-impact product launch video that grabs attention and drives conversions',
    image: '',
    category: TemplateCategory.SalesAndPromotion,
    features: [TemplateFeatures.Avatar, TemplateFeatures.ProductFocus],
  },
  {
    id: TemplateName.BrandReview,
    title: BRAND_REVIEW_VIDEO,
    description:
      'Turn happy customers into brand advocates with an engaging loyal customer video that builds trust and boosts retention',
    image: '',
    category: TemplateCategory.BrandAwareness,
    features: [TemplateFeatures.Avatar],
  },

  {
    id: TemplateName.InfluencerVoiceOver,
    title: PRODUCT_VOICE_OVER_VIDEO,
    description: 'Highlight product features with a persuasive influencer-style voice-over.',
    image: '',
    videoUrl:
      'https://videos.gotolstoy.com/public/0d49234a-1203-442e-adb2-167a94ff278f/2f3602c8-6f73-4e72-96f4-7ef96a868441/2f3602c8-6f73-4e72-96f4-7ef96a868441_w_.mp4?2024-10-27T16:12:26.832Z',
    specs: ['Engaging narrative voice', 'Effective for product demos', 'Focus on key product benefits'],
    category: TemplateCategory.ProductMarketing,
    features: [TemplateFeatures.ProductFocus],
  },
  {
    id: TemplateName.FounderAvatar,
    title: FOUNDER_VIDEO,
    description: 'Connect with audiences through authentic storytelling from your brand’s founder.',
    image: '',
    videoUrl:
      'https://videos.gotolstoy.com/public/0d49234a-1203-442e-adb2-167a94ff278f/bb2b9c71-f097-4490-9cd5-8843224f5cec/bb2b9c71-f097-4490-9cd5-8843224f5cec_w_.mp4?2024-10-17T09:18:56.084Z',
    specs: ['Founder-led narration', 'Personalized brand story', 'Builds brand credibility and trust'],
    category: TemplateCategory.BrandAwareness,
    features: [TemplateFeatures.Avatar],
  },
  {
    id: TemplateName.BeautyShot,
    title: BEAUTY_SHOT,
    description: 'Step by step workflow to create videos from different shots.',
    image: '',
    videoUrl:
      'https://videos.gotolstoy.com/public/0d49234a-1203-442e-adb2-167a94ff278f/bb2b9c71-f097-4490-9cd5-8843224f5cec/bb2b9c71-f097-4490-9cd5-8843224f5cec_w_.mp4?2024-10-17T09:18:56.084Z',
    specs: ['Social-first format', 'Boosts brand relatability', 'Captures attention in seconds'],
    category: TemplateCategory.ProductMarketing,
    features: [TemplateFeatures.ProductFocus],
    videoDuration: '00:15',
  },
];

export const TEMPLATE_USE_CASES = {
  [TemplateName.ProductRotate]: '360°',
  [TemplateName.Highlight]: 'Highlight Features',
  [TemplateName.Lookbook]: 'Lookbook',
  [TemplateName.SizingVideos]: 'Sizing',
  [TemplateName.Promotional]: 'Promotional',
  [TemplateName.ProductComparison]: 'Product Comparison',
  [TemplateName.ProductRelease]: 'Product Release',
  [TemplateName.BrandReview]: 'Brand Review',
  [TemplateName.InfluencerAvatar]: 'Influencer Avatar',
  [TemplateName.InfluencerVoiceOver]: 'Influencer Voice Over',
  [TemplateName.FounderAvatar]: 'Founder Avatar',
  [TemplateName.BeautyShot]: 'Beauty Shot',
} as const;
